import { failureText } from '@shared/lib/failure'
import type { IntegrityPayload } from './lib/integrityReport'
import type { PendingEvent } from './lib/drafts'
import { parseSseBuffer } from './lib/sse'
import type {
  AttemptView,
  HeartbeatResponse,
  InfoResponse,
  LoginResponse,
  MySchedule,
  SessionsResponse,
  SaveResponse,
  TickPayload,
  TimeResponse,
} from './types'

/** Parse a Retry-After header given in delta-seconds; HTTP dates are ignored. */
export function parseRetryAfter(value: string | null): number | null {
  if (!value) return null
  const seconds = Number(value.trim())
  if (!Number.isFinite(seconds) || seconds < 0) return null
  return Math.ceil(seconds)
}

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  /** Seconds the server asked us to wait (Retry-After header), or null. */
  readonly retryAfterSeconds: number | null
  /** Problems the server tied to one form field, by field name. */
  fields: Record<string, string> = {}

  constructor(status: number, code: string, message: string, retryAfterSeconds: number | null = null) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.retryAfterSeconds = retryAfterSeconds
  }
}

async function request<T>(path: string, options: RequestInit = {}, token?: string): Promise<T> {
  const headers = new Headers(options.headers)
  headers.set('Accept', 'application/json')
  if (options.body) headers.set('Content-Type', 'application/json')
  if (token) headers.set('Authorization', `Bearer ${token}`)

  let response: Response
  try {
    response = await fetch(path, { ...options, headers, cache: 'no-store' })
  } catch {
    throw new ApiError(0, 'network_error', 'Tidak dapat menghubungi server. Periksa koneksi.')
  }

  if (!response.ok) {
    let code = 'http_error'
    let ownMessage: string | null = null
    let fields: Record<string, string> = {}
    try {
      const body = (await response.json()) as { error?: { code?: string; message?: string; fields?: Record<string, string> | null } }
      if (body.error?.code) code = body.error.code
      if (body.error?.message) ownMessage = body.error.message
      if (body.error?.fields) fields = body.error.fields
    } catch {
      // Keep the generic message for non-JSON failures.
    }
    const message = failureText(response.status, response.headers.get('X-Request-Id'), ownMessage)
    const failure = new ApiError(response.status, code, message, parseRetryAfter(response.headers.get('Retry-After')))
    failure.fields = fields
    throw failure
  }
  return (await response.json()) as T
}

/** The pupil changes their own password; the old one must be typed again. */
export function changePassword(token: string, current: string, next: string): Promise<{ message: string }> {
  return request<{ message: string }>('/api/v1/me/password', { method: 'POST', body: JSON.stringify({ current, new: next }) }, token)
}

export function getInfo(): Promise<InfoResponse> {
  return request<InfoResponse>('/api/v1/info')
}

export function login(accessCode: string): Promise<LoginResponse> {
  return request<LoginResponse>('/api/v1/session', {
    method: 'POST',
    body: JSON.stringify({ access_code: accessCode }),
  })
}

/** Exchange the one-time token from the management portal for a session of this server. */
export function exchangeHandoff(token: string): Promise<{ role: 'student' | 'proctor' } & Record<string, unknown>> {
  return request('/api/v1/auth/handoff', { method: 'POST', body: JSON.stringify({ token }) })
}

export type UnifiedLoginResponse =
  | ({ role: 'student' } & LoginResponse)
  | { role: 'admin' | 'proctor'; token: string; staff: { display_name: string } }

/** One login for every role: the server decides from the identifier whether it is staff or a participant. */
export function loginUnified(credentials: { identifier: string; secret: string; session_id?: string }): Promise<UnifiedLoginResponse> {
  const body: typeof credentials = { identifier: credentials.identifier, secret: credentials.secret }
  if (credentials.session_id) body.session_id = credentials.session_id
  return request<UnifiedLoginResponse>('/api/v1/auth/login', {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

/** The pupil's own exams, asked with the same credentials as a sign-in (works when no exam is open). */
export function getScheduleByCredentials(credentials: { identifier: string; secret: string }): Promise<MySchedule> {
  return request<MySchedule>('/api/v1/schedule', { method: 'POST', body: JSON.stringify(credentials) })
}

/** The same for a pupil who is already signed in. */
export function getScheduleMe(token: string): Promise<MySchedule> {
  return request<MySchedule>('/api/v1/schedule/me', {}, token)
}

export function getSessions(): Promise<SessionsResponse> {
  return request<SessionsResponse>('/api/v1/sessions')
}

/** Presence ping for the proctor monitor. Callers must ignore failures. */
export function sendHeartbeat(token: string, hidden: boolean): Promise<HeartbeatResponse> {
  return request<HeartbeatResponse>(
    '/api/v1/attempts/current/heartbeat',
    { method: 'POST', body: JSON.stringify({ hidden }) },
    token,
  )
}

/** Report episodes of leaving the page. Evidence only; callers must ignore failures. */
export function sendIntegrity(token: string, events: IntegrityPayload[]): Promise<unknown> {
  return request<unknown>('/api/v1/attempts/current/integrity', { method: 'POST', body: JSON.stringify({ events }) }, token)
}

/**
 * Start (or resume) the attempt. The caller is the start page, so the rules are by definition
 * accepted; the server refuses to create an attempt without it. examToken is the room token,
 * needed only when the session asks for it.
 */
export function startAttempt(token: string, examToken?: string): Promise<AttemptView> {
  const body: { rules_accepted: boolean; token?: string } = { rules_accepted: true }
  if (examToken) body.token = examToken
  return request<AttemptView>('/api/v1/attempts', { method: 'POST', body: JSON.stringify(body) }, token)
}

export function getCurrentAttempt(token: string): Promise<AttemptView> {
  return request<AttemptView>('/api/v1/attempts/current', {}, token)
}

export function getTime(token: string): Promise<TimeResponse> {
  return request<TimeResponse>('/api/v1/attempts/current/time', {}, token)
}

export function saveResponses(token: string, events: PendingEvent[]): Promise<SaveResponse> {
  return request<SaveResponse>(
    '/api/v1/attempts/current/responses',
    { method: 'POST', body: JSON.stringify({ events }) },
    token,
  )
}

export function submitAttempt(token: string, events: PendingEvent[]): Promise<AttemptView> {
  return request<AttemptView>(
    '/api/v1/attempts/current/submit',
    { method: 'POST', body: JSON.stringify({ events }) },
    token,
  )
}

export interface StreamHandlers {
  onTick: (payload: TickPayload) => void
  onError?: (error: unknown) => void
  onUnauthorized?: () => void
}

/**
 * Consume the optional SSE tick stream with a bearer header, which EventSource
 * cannot send. Reconnects with capped backoff. Returns a stop function.
 */
export function openEventStream(token: string, handlers: StreamHandlers): () => void {
  const controller = new AbortController()
  let stopped = false

  const run = async () => {
    let failures = 0
    while (!stopped) {
      try {
        const response = await fetch('/api/v1/events', {
          headers: {
            Accept: 'text/event-stream',
            Authorization: `Bearer ${token}`,
          },
          signal: controller.signal,
          cache: 'no-store',
        })
        if (response.status === 401) {
          handlers.onUnauthorized?.()
          return
        }
        if (!response.ok || !response.body) {
          throw new ApiError(response.status, 'stream_error', 'Stream waktu tidak tersedia.')
        }

        failures = 0
        const reader = response.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ''
        while (!stopped) {
          const { done, value } = await reader.read()
          if (done) break
          buffer += decoder.decode(value, { stream: true })
          const { messages, rest } = parseSseBuffer(buffer)
          buffer = rest
          for (const message of messages) {
            if (message.event !== 'tick') continue
            try {
              handlers.onTick(JSON.parse(message.data) as TickPayload)
            } catch {
              // Ignore malformed frames; polling remains available.
            }
          }
        }
      } catch (error) {
        if (stopped) return
        if (error instanceof DOMException && error.name === 'AbortError') return
        handlers.onError?.(error)
      }

      if (stopped) return
      failures += 1
      const wait = Math.min(15_000, 2_000 * 2 ** Math.min(failures, 3))
      await new Promise((resolve) => setTimeout(resolve, wait))
    }
  }

  void run()

  return () => {
    stopped = true
    controller.abort()
  }
}
